import { cn } from "@/lib/cn";

// ds28 §3.4 — "LaTeX preview footer" shared by the Format and Insert mega
// menus. Pinned to the bottom of each mega panel (separated by a hairline).
//   Left:  monospace preview of the LaTeX the currently-hovered/focused item
//          emits (placeholder when nothing is hovered).
//   Right: a small "Show LaTeX" toggle, persisted in localStorage
//          (`lilia-mega-show-latex`, default ON). When OFF the snippet area is
//          hidden and the footer collapses to just the toggle row.
// "This teaches syntax without leaving the menu — a key, deliberate feature."

// Shared localStorage key + helpers so both megas read/write the same pref.
export const SHOW_LATEX_KEY = "lilia-mega-show-latex";

export function readShowLatexPref(): boolean {
	if (typeof window === "undefined") return true;
	try {
		// Default ON — only an explicit "false" turns it off.
		return window.localStorage.getItem(SHOW_LATEX_KEY) !== "false";
	} catch {
		return true;
	}
}

export function writeShowLatexPref(value: boolean): void {
	try {
		window.localStorage.setItem(SHOW_LATEX_KEY, value ? "true" : "false");
	} catch {
		/* ignore quota / privacy-mode failures */
	}
}

interface MegaFooterProps {
	/** LaTeX snippet of the currently hovered/focused item ("" → placeholder). */
	hoveredLatex: string;
	/** Persisted "Show LaTeX" preference. */
	showLatex: boolean;
	/** Toggle handler — caller persists + re-renders. */
	onToggle: (next: boolean) => void;
}

export function MegaFooter({ hoveredLatex, showLatex, onToggle }: MegaFooterProps) {
	return (
		<div
			data-testid="mega-footer"
			className="flex items-center gap-2.5 border-t border-[var(--lilia-border)] bg-[var(--lilia-code-bg)] px-[14px] py-[9px] min-h-[38px]"
		>
			{/* Left — LaTeX preview (hidden when the pref is OFF). The footer row
			    itself carries the code-bg (ds30 .lilia-mega-footer); the <code>
			    is transparent so it reads as inline source, not a nested chip. */}
			{showLatex && (
				<div className="min-w-0 flex-1">
					{hoveredLatex ? (
						<code
							data-testid="mega-footer-latex"
							className="block truncate text-[12px] text-[var(--lilia-code-command)]"
							style={{ fontFamily: "var(--lilia-font-mono)" }}
						>
							{hoveredLatex}
						</code>
					) : (
						<span
							data-testid="mega-footer-placeholder"
							className="block truncate text-[12px] italic text-[var(--lilia-text-muted)]"
						>
							Hover an item to preview its LaTeX
						</span>
					)}
				</div>
			)}

			{/* Right — "Show LaTeX" toggle. */}
			<label
				className={cn(
					"flex shrink-0 cursor-pointer select-none items-center gap-1.5 text-[11px] font-medium text-[var(--lilia-text-secondary)]",
					!showLatex && "flex-1 justify-end",
				)}
			>
				<input
					type="checkbox"
					data-testid="mega-latex-toggle"
					checked={showLatex}
					onChange={(e) => onToggle(e.target.checked)}
					className="h-3.5 w-3.5 cursor-pointer accent-[var(--lilia-primary)]"
				/>
				Show LaTeX
			</label>
		</div>
	);
}
